import React, { FC, useMemo } from 'react';
import { convertLineChartToECharts } from '../adapters/echartsAdapter';
import { EChartsContainer, useSeriesChartEvents } from '../shared';
import { LineChartProps } from '../types';

/**
 * ScalarLineChart
 *
 * A generic line chart component with a library-agnostic API.
 * Internally uses ECharts, but the API is designed to be easily swappable.
 *
 * @example
 * ```tsx
 * <ScalarLineChart
 *   series={[
 *     {
 *       id: 'series1',
 *       label: 'Sales',
 *       data: [
 *         { x: 'Jan', y: 100 },
 *         { x: 'Feb', y: 200 },
 *       ],
 *       color: '#3498db',
 *     },
 *   ]}
 *   config={{
 *     title: 'Monthly Sales',
 *     xAxis: { label: 'Month' },
 *     yAxis: { label: 'Amount' },
 *   }}
 * />
 * ```
 */
const ScalarLineChart: FC<LineChartProps> = ({
  series,
  config,
  area = false,
  showPoints = true,
  smooth = false,
  className,
  style,
  loading = false,
  error = null,
  onDataPointClick,
  onSeriesClick,
  onChartReady,
}) => {
  const echartsOption = useMemo(
    () =>
      convertLineChartToECharts({
        series,
        config,
        area,
        showPoints,
        smooth,
      }),
    [series, config, area, showPoints, smooth]
  );

  const onEvents = useSeriesChartEvents(series, onDataPointClick, onSeriesClick);

  return (
    <EChartsContainer
      option={echartsOption}
      className={className}
      style={style}
      error={error}
      loading={loading}
      onEvents={onEvents}
      onChartReady={onChartReady}
    />
  );
};

export default ScalarLineChart;
